import React from 'react'
import { Player, BuffType } from '../game/GameEngine'
import { Shield, Zap, Sword, Snowflake, Star } from 'lucide-react'

interface PlayerCardProps {
  player: Player
  isPlayerTurn: boolean
  title: string
  position: 'left' | 'right'
}

// AGC: Player card component displaying player stats and status
// AGC: Shows health, level, buffs, and current choices
export const PlayerCard: React.FC<PlayerCardProps> = ({ 
  player, 
  isPlayerTurn, 
  title, 
  position 
}) => {
  // AGC: Get buff icon component
  const getBuffIcon = (buff: BuffType) => {
    switch (buff) {
      case 'SHIELD':
        return <Shield className="w-4 h-4 text-blue-400" />
      case 'PIERCE':
        return <Sword className="w-4 h-4 text-red-400" />
      case 'HASTE':
        return <Zap className="w-4 h-4 text-yellow-400" />
      case 'FROST':
        return <Snowflake className="w-4 h-4 text-cyan-400" />
      default:
        return null
    }
  }

  // AGC: Calculate health percentage for health bar
  const healthPercentage = (player.ep / 100) * 100

  // AGC: Get health bar color based on health percentage
  const getHealthBarColor = () => {
    if (healthPercentage > 60) return 'from-green-500 to-green-400'
    if (healthPercentage > 30) return 'from-yellow-500 to-yellow-400'
    return 'from-red-500 to-red-400'
  }

  // AGC: Get origami choice image
  const getOrigamiImage = (choice?: string) => {
    switch (choice) {
      case 'ROCK': return '/rock.png'
      case 'PAPER': return '/paper.png'
      case 'SCISSORS': return '/scissor.png'
      default: return null
    }
  }

  // AGC: Get element choice emoji
  const getElementEmoji = (choice?: string) => {
    switch (choice) {
      case 'FIRE': return '🔥'
      case 'WATER': return '💧'
      case 'ICE': return '❄️'
      case 'WIND': return '💨'
      default: return '❓'
    }
  }

  return (
    <div className={`
      battle-card p-6 
      ${isPlayerTurn ? 'ring-2 ring-blue-400 animate-glow' : ''}
      ${position === 'right' ? 'lg:order-3' : ''}
    `}>
      {/* AGC: Player header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        <div className="flex items-center space-x-2">
          <Star className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-400 font-semibold">Lv.{player.level}</span>
        </div>
      </div>

      {/* AGC: Health bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-300 mb-2">
          <span>Energy Points</span>
          <span>{player.ep}/100</span>
        </div>
        <div className="health-bar">
          <div 
            className={`health-fill bg-gradient-to-r ${getHealthBarColor()}`}
            style={{ width: `${healthPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* AGC: Active buffs */}
      {player.buffs.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Active Buffs</h4>
          <div className="flex flex-wrap gap-2">
            {player.buffs.map((buff, index) => (
              <div
                key={index}
                className="flex items-center space-x-1 bg-white/10 rounded-full px-3 py-1"
              >
                {getBuffIcon(buff)}
                <span className="text-xs text-gray-200">{buff}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AGC: Current choices display */}
      {player.choice && (
        <div className="border-t border-white/20 pt-4">
          <h4 className="text-sm font-semibold text-gray-300 mb-3">Current Choice</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center">
              <div className="w-8 h-8 mx-auto mb-1 flex items-center justify-center">
                {getOrigamiImage(player.choice.origami) ? (
                  <img src={getOrigamiImage(player.choice.origami)} alt={player.choice.origami} className="w-full h-full object-contain" />
                ) : (
                  <span className="text-2xl">❓</span>
                )}
              </div>
              <div className="text-xs text-gray-300">{player.choice.origami}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-1">{getElementEmoji(player.choice.element)}</div>
              <div className="text-xs text-gray-300">{player.choice.element}</div>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Waiting indicator for opponent */}
      {!player.choice && title === "Opponent" && (
        <div className="text-center py-8">
          <div className="animate-pulse text-4xl mb-2">🤖</div>
          <p className="text-gray-400 text-sm">AI is thinking...</p>
        </div>
      )}
    </div>
  )
}
